import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import VideoPlayer from './VideoPlayer';
import { fileUrl } from './api.js';
import { getMimeType, getPreviewKind, formatSize } from './file-utils';

const TEXT_PREVIEW_LIMIT = 2 * 1024 * 1024;
const IS_MOBILE_UA = /Android|iPhone|iPad|iPod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');

function useBlobPreview(fileManager, row, enabled) {
    const [state, setState] = useState({ url: null, blob: null, progress: 0, error: null, loading: enabled });
    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        let objectUrl = null;
        setState({ url: null, blob: null, progress: 0, error: null, loading: true });
        fileManager.getPreviewBlob(row.path, getMimeType(row.name), (loaded, total) => {
            if (!cancelled) setState((s) => ({ ...s, progress: total ? Math.round((loaded / total) * 100) : 0 }));
        }).then((blob) => {
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            setState({ url: objectUrl, blob, progress: 100, error: null, loading: false });
        }).catch((error) => {
            if (!cancelled) setState({ url: null, blob: null, progress: 0, error: error.message || String(error), loading: false });
        });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [fileManager, row.path, row.name, enabled]);
    return state;
}

function Loading({ progress }) {
    return (
        <Box sx={{ width: '100%', py: 4 }}>
            <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>Memuat preview... {progress}%</Typography>
            <LinearProgress variant="determinate" value={progress} />
        </Box>
    );
}

function ImagePreview({ fileManager, row }) {
    const { url, progress, error, loading } = useBlobPreview(fileManager, row, true);
    if (loading) return <Loading progress={progress} />;
    if (error) return <Alert severity="error">Gagal memuat gambar: {error}</Alert>;
    return <img src={url} alt={row.name} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block', margin: '0 auto' }} />;
}

function AudioPreview({ fileManager, row }) {
    const [useBlob, setUseBlob] = useState(Boolean(row.enc));
    const blob = useBlobPreview(fileManager, row, useBlob);
    const src = useBlob ? blob.url : fileUrl('stream', fileManager.userId, row.path);
    if (useBlob && blob.loading) return <Loading progress={blob.progress} />;
    if (useBlob && blob.error) return <Alert severity="error">Gagal memuat audio: {blob.error}</Alert>;
    return (
        <Box sx={{ width: '100%', py: 3 }}>
            <audio
                src={src}
                controls
                autoPlay
                style={{ width: '100%' }}
                onError={() => { if (!useBlob && row.size < 200 * 1024 * 1024) setUseBlob(true); }}
            />
        </Box>
    );
}

async function decodeText(buffer) {
    const bytes = new Uint8Array(buffer);
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes);
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes);
    return new TextDecoder('utf-8').decode(bytes);
}

// Isi file teks ditampilkan sebagai TEKS POLOS saja (bukan HTML), supaya
// file .html/.svg dari mana pun tidak bisa menjalankan script di web Disbox.
function TextPreview({ fileManager, row }) {
    const [state, setState] = useState({ text: null, error: null, truncated: false });
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                let buffer;
                let truncated = false;
                if (!row.enc) {
                    const response = await fetch(fileUrl('stream', fileManager.userId, row.path), {
                        headers: row.size > TEXT_PREVIEW_LIMIT ? { Range: `bytes=0-${TEXT_PREVIEW_LIMIT - 1}` } : {},
                    });
                    if (!response.ok) throw new Error(`Bot membalas status ${response.status}`);
                    buffer = await response.arrayBuffer();
                    truncated = row.size > TEXT_PREVIEW_LIMIT;
                } else {
                    if (row.size > TEXT_PREVIEW_LIMIT) throw new Error('File teks terenkripsi ini terlalu besar untuk dipreview. Download saja.');
                    const blob = await fileManager.getPreviewBlob(row.path, 'text/plain');
                    buffer = await blob.arrayBuffer();
                }
                const text = await decodeText(buffer);
                if (!cancelled) setState({ text, error: null, truncated });
            } catch (error) {
                if (!cancelled) setState({ text: null, error: error.message || String(error), truncated: false });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fileManager, row.path, row.enc, row.size]);

    const onCopy = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(state.text);
            else {
                const area = document.createElement('textarea');
                area.value = state.text;
                document.body.appendChild(area);
                area.select();
                document.execCommand('copy');
                document.body.removeChild(area);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* abaikan */ }
    };

    if (state.error) return <Alert severity="error">Gagal memuat teks: {state.error}</Alert>;
    if (state.text === null) return <Loading progress={0} />;
    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                    {formatSize(row.size)}{state.truncated ? ` · hanya ${formatSize(TEXT_PREVIEW_LIMIT)} pertama yang ditampilkan` : ''}
                </Typography>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={onCopy}>{copied ? 'Tersalin' : 'Salin'}</Button>
            </Box>
            <Box
                component="pre"
                sx={{
                    m: 0, p: 1.5, maxHeight: '70vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: 13, bgcolor: 'action.hover', borderRadius: 1,
                }}
            >
                {state.text}
            </Box>
        </Box>
    );
}

// PDF: di desktop tampil langsung (viewer PDF bawaan browser); di HP browser
// biasanya tidak bisa menampilkan PDF di dalam halaman, jadi dibuka di tab
// baru (diteruskan ke aplikasi PDF di HP).
function PdfPreview({ fileManager, row, onDownload }) {
    const blob = useBlobPreview(fileManager, row, Boolean(row.enc));
    const src = row.enc ? blob.url : fileUrl('stream', fileManager.userId, row.path);
    if (row.enc && blob.loading) return <Loading progress={blob.progress} />;
    if (row.enc && blob.error) return <Alert severity="error">Gagal memuat PDF: {blob.error}</Alert>;
    if (IS_MOBILE_UA) {
        return (
            <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Browser HP tidak bisa menampilkan PDF di sini.</Typography>
                <Button variant="contained" startIcon={<OpenInNewIcon />} href={src} target="_blank" rel="noopener">Buka PDF</Button>
                <Button startIcon={<DownloadIcon />} onClick={() => onDownload(row)}>Download</Button>
            </Box>
        );
    }
    return <iframe title={row.name} src={src} style={{ width: '100%', height: '75vh', border: 0, borderRadius: 8, background: '#fff' }} />;
}

export default function PreviewDialog({ open, row, fileManager, isMobile, onClose, onNavigate, onDownload }) {
    const kind = row ? getPreviewKind(row.name) : null;
    const wide = kind === 'text' || kind === 'pdf' || kind === 'video';
    return (
        <Dialog open={open && Boolean(row)} onClose={onClose} maxWidth={wide ? 'lg' : 'md'} fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 1.5 }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={row ? row.name : ''}>
                    {row ? row.name : ''}
                </Box>
                <IconButton size="small" aria-label="Tutup" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, px: { xs: 1, sm: 3 } }}>
                {row && kind === 'video' && (
                    <VideoPlayer key={row.path} fileManager={fileManager} row={row} onNavigate={onNavigate} onDownload={onDownload} />
                )}
                {row && kind === 'image' && <ImagePreview key={row.path} fileManager={fileManager} row={row} />}
                {row && kind === 'audio' && <AudioPreview key={row.path} fileManager={fileManager} row={row} />}
                {row && kind === 'text' && <TextPreview key={row.path} fileManager={fileManager} row={row} />}
                {row && kind === 'pdf' && <PdfPreview key={row.path} fileManager={fileManager} row={row} onDownload={onDownload} />}
                {row && !kind && <Alert severity="info">File ini tidak bisa dipreview. Download untuk membukanya.</Alert>}
            </DialogContent>
        </Dialog>
    );
}
